import dayjs from "dayjs";

import { Coupon, HubPlan, PlanInfo } from "data/models";
import { formatPrice } from "utils/format";

export type ChangeType = "upgrade" | "downgrade" | "same" | "no_selection";

export type ChangeItem = {
  lhs: string;
  rhs?: string;
  hidden?: boolean;
};

export type ChangeSummary = {
  infoText: string;
  changeType: ChangeType;
  subscription: ChangeItem[];
  charges: ChangeItem[];
  totalDue: number | string;
  trialChargeWarning: string | null;
};

export const formatSummaryDate = (dt: dayjs.Dayjs) => dt.format("D MMM YYYY");

export const getPlan = (
  productKey: string,
  cadence: "month" | "year",
  planInfo: PlanInfo,
) => {
  const validPrices = planInfo.availablePlans.filter(
    (x) => !x.deprecated && !x.externally_managed,
  );
  const productPrices = validPrices.filter((x) => x.product_key === productKey);
  const matchingPrice = productPrices.filter((x) => x.cadence === cadence);
  return matchingPrice[0] ?? null;
};

export const getPlanByPriceLookupId = (
  priceLookupId: string,
  plans: HubPlan[],
) => plans.find((plan) => plan.price_lookup_id === priceLookupId) ?? null;

export const getSuccessText = (
  selectedPlan: HubPlan | null,
  currentPlan: HubPlan | null,
) => {
  if (selectedPlan === null) return "No new plan selected";
  if (currentPlan === null)
    return `Successfully started a ${selectedPlan.display_name} subscription!`;
  if (currentPlan.tier > selectedPlan.tier)
    return `Successfully scheduled downgrade from ${currentPlan.display_name} to ${selectedPlan.display_name}`;
  return `Successfully upgraded from ${currentPlan.display_name} to ${selectedPlan.display_name}`;
};

export const getChangeType = (
  selectedPlan: HubPlan | null,
  currentPlan: HubPlan | null,
  onAdminTrial = false,
): ChangeType => {
  if (selectedPlan === null) return "no_selection";
  if (currentPlan === null) return "upgrade";
  if (selectedPlan.tier === currentPlan.tier && !onAdminTrial) return "same";
  if (currentPlan.tier > selectedPlan.tier) return "downgrade";
  return "upgrade";
};

export const isCouponValid = (priceKey: string, coupon?: Coupon): boolean =>
  !!coupon &&
  !!coupon.applies_to &&
  coupon.applies_to.price_keys.includes(priceKey);

export const calculateCouponDiscount = (
  subscriptionPrice: number,
  priceKey: string,
  coupon?: Coupon,
): number => {
  if (coupon && isCouponValid(priceKey, coupon)) {
    if (coupon.amount_off) return coupon.amount_off / 100;
    if (coupon.percent_off) return (subscriptionPrice * coupon.percent_off) / 100;
  }
  return 0;
};

export const generateCouponMessage = (coupon: Coupon) => {
  const discount = coupon.amount_off
    ? `$${coupon.amount_off / 100}`
    : `${coupon.percent_off}%`;
  return `${discount} discount has been applied. Duration: ${coupon.duration}`;
};

interface BuildChangeSummaryArgs {
  selectedPlan: HubPlan | null;
  currentRenewalDate: number | undefined;
  changeType: ChangeType;
  trialSelected: boolean;
  newOverageBudget: number;
  overageDollarsSpent: number;
  coupon?: Coupon;
}

export const buildChangeSummary = ({
  selectedPlan,
  currentRenewalDate,
  changeType,
  trialSelected,
  newOverageBudget,
  overageDollarsSpent,
  coupon,
}: BuildChangeSummaryArgs): ChangeSummary => {
  const startDateDt =
    changeType === "upgrade"
      ? dayjs()
      : dayjs((currentRenewalDate ?? 1) * 1000);
  const cadence = selectedPlan?.cadence ?? "month";
  const renewalDate = startDateDt.add(1, cadence);

  if (changeType !== "upgrade" && changeType !== "downgrade") {
    return {
      infoText: "No plan selected",
      changeType,
      subscription: [],
      charges: [],
      totalDue: 0,
      trialChargeWarning: null,
    };
  }

  const infoText =
    changeType === "upgrade"
      ? "These changes will apply immediately, and you will be charged the total due below"
      : "These changes will take effect at the end of your current billing cycle. The due amount will be charged on the renewal date, along with any additional overage incurred";

  const couponValid = isCouponValid(selectedPlan?.price_lookup_id ?? "", coupon);
  const planAmountDue = trialSelected ? 0 : selectedPlan?.dollar_cost ?? 0;
  const discount = calculateCouponDiscount(
    selectedPlan?.dollar_cost ?? 0,
    selectedPlan?.price_lookup_id ?? "",
    coupon,
  );

  const totalDue = selectedPlan
    ? formatPrice(
        overageDollarsSpent +
          planAmountDue -
          Math.min(discount, planAmountDue),
      )
    : "";

  return {
    infoText,
    changeType,
    subscription: [
      { lhs: `Subscribe to ${selectedPlan?.display_name}` },
      { lhs: "Start Date", rhs: formatSummaryDate(startDateDt) },
      { lhs: "Automatically Renews", rhs: formatSummaryDate(renewalDate) },
      {
        lhs: "Starting Credit Balance",
        rhs: `${selectedPlan?.credit_amount.toLocaleString()} credits`,
      },
      { lhs: "Overage Budget", rhs: `Up to $${newOverageBudget}` },
    ],
    charges: [
      {
        lhs: "Previous Subscription Overage",
        rhs: formatPrice(overageDollarsSpent),
      },
      {
        lhs: trialSelected
          ? `${selectedPlan?.display_name} Subscription - Trial `
          : `${selectedPlan?.display_name} Subscription`,
        rhs: formatPrice(planAmountDue),
      },
      {
        lhs: coupon?.name ?? "",
        rhs: `-$${discount}`,
        hidden: !couponValid,
      },
      {
        lhs: "GST (10% Inclusive)",
        rhs: formatPrice((planAmountDue + overageDollarsSpent) / 11),
      },
    ],
    totalDue,
    trialChargeWarning: trialSelected
      ? `Your trial automatically converts to a paid plan at ${formatPrice(
          selectedPlan?.dollar_cost ?? 0,
        )}/${selectedPlan?.cadence} on the ${formatSummaryDate(
          renewalDate,
        )}, unless cancelled prior`
      : null,
  };
};
